import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Download, MessageCircle, Mail, Link2, Copy, ArrowLeft, Loader2, FileText,
  Edit, Files, CheckCircle2,
} from "lucide-react";
import { Button } from "../components/ui/button";

const STATUS_STYLE = {
  pending: "bg-blue-50 text-blue-700",
  draft: "bg-slate-100 text-slate-600",
  in_progress: "bg-amber-50 text-amber-700",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-600",
};

export default function ReferralView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(true);

  const origin = window.location.origin;
  const token = localStorage.getItem("referu_token");
  const pdfUrl = ref ? `${API}/referrals/${id}/pdf?origin=${encodeURIComponent(origin)}&token=${ref.public_token}` : "";
  const publicLink = ref ? `${origin}/r/${ref.public_token}` : "";

  useEffect(() => {
    api.get(`/referrals/${id}`).then((r) => setRef(r.data)).catch(() => toast.error("Referral not found")).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!ref) return <div className="p-8 text-center text-slate-500">Referral not found.</div>;

  const studyText = ref.studies?.map((s) => s.name).join(", ");
  const whatsappMsg = encodeURIComponent(
    `Radiology Referral ${ref.referral_number}\nPatient: ${ref.patient?.first_name} ${ref.patient?.last_name}\nStudy: ${studyText}\nPriority: ${ref.priority}\nView: ${publicLink}`
  );
  const waNumber = ref.centre_snapshot?.whatsapp?.replace(/\D/g, "");

  const copyLink = async () => { try { await navigator.clipboard.writeText(publicLink); } catch (e) {} toast.success("Secure link copied"); };
  const emailReferral = async (to, label) => {
    if (!to) { toast.error(`No ${label} email on file`); return; }
    try { await api.post(`/referrals/${id}/email`, { to, origin, recipient_type: label }); toast.success(`Referral emailed to ${label}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send email"); }
  };

  const duplicate = async () => {
    try {
      const payload = { patient: ref.patient, clinical: ref.clinical, studies: ref.studies, priority: ref.priority, contrast: ref.contrast, centre_id: ref.centre_id, status: "pending" };
      const res = await api.post("/referrals", payload);
      toast.success("Referral duplicated");
      navigate(`/referrals/${res.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not duplicate"); }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-800 text-sm mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-head">{ref.patient?.first_name} {ref.patient?.last_name}</h1>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_STYLE[ref.status]}`}>{ref.status}</span>
          </div>
          <p className="text-slate-500 mt-1">{ref.referral_number} · {ref.created_at?.slice(0, 10)}</p>
        </div>
        <div className="flex gap-2">
          {user?.role === "doctor" && (
            <>
              <Button data-testid="edit-referral-btn" variant="outline" onClick={() => navigate(`/referrals/${id}/edit`)} className="rounded-xl"><Edit className="w-4 h-4 mr-1" /> Edit</Button>
              <Button data-testid="duplicate-btn" variant="outline" onClick={duplicate} className="rounded-xl"><Files className="w-4 h-4 mr-1" /> Duplicate</Button>
            </>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 space-y-5">
          <Card title="Examination Requested">
            <div className="flex flex-wrap gap-2">
              {ref.studies?.map((s) => <span key={s.name} className="text-sm font-medium bg-blue-600 text-white px-3 py-1.5 rounded-full">{s.name}</span>)}
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Field k="Priority" v={ref.priority} />
              <Field k="Contrast" v={ref.contrast} />
            </div>
          </Card>
          <Card title="Patient Details">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field k="Date of Birth" v={ref.patient?.dob} />
              <Field k="Age" v={ref.patient?.age} />
              <Field k="Gender" v={ref.patient?.gender} />
              <Field k="Phone" v={ref.patient?.phone} />
              <Field k="Email" v={ref.patient?.email} />
              <Field k="Address" v={ref.patient?.address} />
            </div>
          </Card>
          <Card title="Clinical Information">
            <div className="space-y-3">
              <Field k="Clinical History" v={ref.clinical?.clinical_history} />
              <Field k="Working Diagnosis" v={ref.clinical?.working_diagnosis} />
              <Field k="Previous Imaging" v={ref.clinical?.previous_imaging} />
              <Field k="Allergies" v={ref.clinical?.allergies} />
              <Field k="Chronic Illness" v={ref.clinical?.chronic_illness} />
              {ref.clinical?.pregnancy_status && <Field k="Pregnancy" v={`${ref.clinical.pregnancy_status} · LMP ${ref.clinical.lmp || "—"}`} />}
            </div>
          </Card>
          {ref.centre_snapshot && (
            <Card title="Radiology Centre">
              <Field k="Centre" v={ref.centre_snapshot.name} />
              <div className="grid sm:grid-cols-2 gap-4 mt-3">
                <Field k="Phone" v={ref.centre_snapshot.phone} />
                <Field k="Email" v={ref.centre_snapshot.email} />
                <Field k="Address" v={ref.centre_snapshot.address} />
              </div>
            </Card>
          )}
          <Card title="Referring Physician">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field k="Doctor" v={ref.doctor_snapshot?.full_name} />
              <Field k="Reg. Number" v={ref.doctor_snapshot?.medical_reg_number} />
              <Field k="Clinic" v={ref.doctor_snapshot?.clinic_name} />
              <Field k="Phone" v={ref.doctor_snapshot?.phone} />
            </div>
            <div className="mt-4">
              <p className="text-xs text-slate-400 mb-1.5">Signature</p>
              {ref.doctor_snapshot?.signature_file_id ? (
                <img data-testid="referral-signature" src={`${API}/files/${ref.doctor_snapshot.signature_file_id}`} alt="Doctor signature" className="h-20 object-contain border-b border-slate-200 pb-1" />
              ) : (
                <p className="text-sm text-slate-400 italic">No signature on file — add one in your <a href="/profile" className="text-blue-600 underline">profile</a>.</p>
              )}
            </div>
          </Card>
        </div>

        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-semibold text-slate-900 font-head flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" /> Referral PDF</h3>
            <p className="text-sm text-slate-500 mt-1">Branded PDF with QR code.</p>
            <a href={pdfUrl} download target="_blank" rel="noreferrer">
              <Button data-testid="download-pdf-btn" className="w-full mt-4 rounded-xl h-12 bg-blue-600 hover:bg-blue-700"><Download className="w-4 h-4 mr-1.5" /> Download PDF</Button>
            </a>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-semibold text-slate-900 font-head">Share Referral</h3>
            <div className="space-y-2.5 mt-4">
              {waNumber && (
                <a href={`https://wa.me/${waNumber}?text=${whatsappMsg}`} target="_blank" rel="noreferrer">
                  <Button data-testid="share-whatsapp-btn" variant="outline" className="w-full rounded-xl h-11 justify-start border-slate-200"><MessageCircle className="w-4 h-4 mr-2 text-emerald-600" /> WhatsApp Centre</Button>
                </a>
              )}
              <a href={`https://wa.me/?text=${whatsappMsg}`} target="_blank" rel="noreferrer">
                <Button data-testid="share-whatsapp-patient-btn" variant="outline" className="w-full rounded-xl h-11 justify-start border-slate-200 mt-2.5"><MessageCircle className="w-4 h-4 mr-2 text-emerald-600" /> WhatsApp Patient</Button>
              </a>
              <Button data-testid="email-patient-btn" onClick={() => emailReferral(ref.patient?.email, "patient")} variant="outline" className="w-full rounded-xl h-11 justify-start border-slate-200 mt-2.5"><Mail className="w-4 h-4 mr-2 text-blue-600" /> Email Patient</Button>
              <Button data-testid="email-centre-btn" onClick={() => emailReferral(ref.centre_snapshot?.email, "centre")} variant="outline" className="w-full rounded-xl h-11 justify-start border-slate-200 mt-2.5"><Mail className="w-4 h-4 mr-2 text-blue-600" /> Email Centre</Button>
              <Button data-testid="copy-link-btn" onClick={copyLink} variant="outline" className="w-full rounded-xl h-11 justify-start border-slate-200 mt-2.5"><Link2 className="w-4 h-4 mr-2 text-slate-600" /> Copy View Link</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
      <p className="text-xs font-semibold tracking-[0.15em] uppercase text-blue-600 mb-4">{title}</p>
      {children}
    </div>
  );
}
function Field({ k, v }) {
  return (
    <div>
      <p className="text-xs text-slate-400">{k}</p>
      <p className="text-sm text-slate-800 font-medium mt-0.5">{v || "—"}</p>
    </div>
  );
}
